import React, { useState, useEffect, useCallback } from 'react';
import { api } from './App';
import './LawDrillsReview.css';

export default function LawDrillsReview() {
  const [loading, setLoading]   = useState(true);
  const [batch, setBatch]       = useState(null);
  const [topics, setTopics]     = useState([]);
  const [allTopics, setAllTopics] = useState([]);
  const [overrides, setOverrides] = useState({}); // { result_id: sqetrack_topic_id }
  const [saving, setSaving]     = useState(false);
  const [feedback, setFeedback] = useState(null); // { type: 'success'|'error', msg }

  const load = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const data = await api('/api/import/lawdrills/last');
      setBatch(data.batch);
      setTopics(data.topics || []);
      setAllTopics(data.all_topics || []);
    } catch {
      setFeedback({ type: 'error', msg: 'Could not load import data. Check your connection.' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Group topics: unmatched first, then by SQETrack module
  const groups = React.useMemo(() => {
    const unmatched = topics.filter(t => !t.sqetrack_topic_id);
    const matched   = topics.filter(t =>  t.sqetrack_topic_id);
    const byModule  = {};
    for (const t of matched) {
      const mod = t.sqetrack_module || 'Other';
      (byModule[mod] = byModule[mod] || []).push(t);
    }
    return [
      ...(unmatched.length ? [{ label: '⚠ Unmatched', rows: unmatched }] : []),
      ...Object.entries(byModule).map(([label, rows]) => ({ label, rows })),
    ];
  }, [topics]);

  const unmatchedCount = topics.filter(t => !t.sqetrack_topic_id && !overrides[t.id]).length;
  const pendingCount   = Object.keys(overrides).length;

  async function handleSave() {
    if (!pendingCount) return;
    setSaving(true);
    setFeedback(null);
    try {
      const payload = Object.entries(overrides).map(([result_id, sqetrack_topic_id]) => ({
        result_id: parseInt(result_id, 10),
        sqetrack_topic_id: sqetrack_topic_id ? parseInt(sqetrack_topic_id, 10) : null,
      }));
      const data = await api('/api/import/lawdrills/override', {
        method: 'POST',
        body: JSON.stringify({ overrides: payload }),
      });
      if (data.error) throw new Error(data.error || 'Save failed');
      setFeedback({ type: 'success', msg: `${data.applied} mapping${data.applied !== 1 ? 's' : ''} saved. Future imports will use these matches automatically.` });
      setOverrides({});
      await load(); // refresh topic list with updated confidence
    } catch (e) {
      setFeedback({ type: 'error', msg: e.message });
    }
    setSaving(false);
  }

  if (loading) return <div className="ldr-wrap"><p className="ldr-meta" style={{padding:'40px 0',textAlign:'center'}}>Loading…</p></div>;

  if (!batch) {
    return (
      <div className="ldr-wrap">
        <div className="ldr-empty">
          <strong>No Law Drills import yet</strong>
          Use the SQETrack Import Chrome extension to import your Law Drills scores, then return here to review the topic matches.
        </div>
      </div>
    );
  }

  const importedAt = batch.imported_at ? new Date(batch.imported_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '';

  return (
    <div className="ldr-wrap">
      <div className="ldr-header">
        <div>
          <div className="ldr-title">Law Drills — Review Topic Matches</div>
          <div className="ldr-meta">Imported {importedAt} · {batch.topic_count} topics</div>
        </div>
        <div className="ldr-pills">
          <span className="ldr-pill ldr-pill-paper">{batch.paper}</span>
          {unmatchedCount > 0 && (
            <span className="ldr-pill ldr-pill-unmatched">{unmatchedCount} unmatched</span>
          )}
          {unmatchedCount === 0 && (
            <span className="ldr-pill ldr-pill-ok">All matched</span>
          )}
        </div>
      </div>

      {feedback && (
        <div className={`ldr-feedback ${feedback.type}`}>{feedback.msg}</div>
      )}

      {groups.map(({ label, rows }) => (
        <div className="ldr-group" key={label}>
          <div className="ldr-group-heading">{label}</div>
          <table className="ldr-table">
            <thead>
              <tr>
                <th>Law Drills topic</th>
                <th>Score</th>
                <th>SQETrack match</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(t => {
                const currentTopicId = overrides.hasOwnProperty(t.id)
                  ? overrides[t.id]
                  : t.sqetrack_topic_id;
                const conf = t.sqetrack_topic_id ? t.confidence : 'none';

                return (
                  <tr key={t.id}>
                    <td className="ldr-source">
                      {t.source_label}
                      {t.attempted != null && (
                        <span style={{color:'#9ca3af',marginLeft:6,fontSize:12}}>
                          {t.score_pct != null ? `${Math.round(t.score_pct)}%` : '—'} ({t.attempted}q)
                        </span>
                      )}
                    </td>
                    <td className="ldr-score">
                      {t.score_pct != null ? `${Math.round(t.score_pct)}%` : '—'}
                    </td>
                    <td className="ldr-match-cell">
                      <select
                        className="ldr-select"
                        value={currentTopicId || ''}
                        onChange={e => setOverrides(prev => ({
                          ...prev,
                          [t.id]: e.target.value || null,
                        }))}
                      >
                        <option value="">— Not matched —</option>
                        {allTopics.map(at => (
                          <option key={at.id} value={at.id}>{at.label}</option>
                        ))}
                      </select>
                      {!overrides.hasOwnProperty(t.id) && t.sqetrack_topic_id && (
                        <span className={`conf-badge conf-${conf}`}>{conf}</span>
                      )}
                      {overrides.hasOwnProperty(t.id) && (
                        <span className="conf-badge" style={{background:'#EFF6FF',color:'#1D4ED8'}}>edited</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ))}

      <div className="ldr-actions">
        <button
          className="ldr-save-btn"
          disabled={saving || !pendingCount}
          onClick={handleSave}
        >
          {saving ? 'Saving…' : `Save ${pendingCount || ''} change${pendingCount !== 1 ? 's' : ''}`}
        </button>
        {pendingCount > 0 && (
          <span className="ldr-cancel-note">
            <button
              style={{background:'none',border:'none',color:'#6b7280',cursor:'pointer',fontSize:13,padding:0}}
              onClick={() => setOverrides({})}
            >
              Discard changes
            </button>
          </span>
        )}
      </div>
    </div>
  );
}
